import { PYTHON_DIRECTORIES } from "../interfaces/python.interface";

/**
 * PythonEnvironmentHelper - Simplified environment configuration (Phase 2)
 *
 * Radically simplified from 1220 lines to 50 lines by removing:
 * - Security proxy (300 lines) - overengineering, not used
 * - Project isolation (400 lines) - not used in production
 * - File operations (200 lines) - duplicates Phase 1's FileSystemHelper
 * - Cleanup logic (270 lines) - duplicates Phase 1's Journal
 *
 * Kept only essential configuration methods.
 */
export class PythonEnvironmentHelper {

  /**
   * Get directory paths configuration
   * This method is used across the codebase for consistent path references
   * Updated: Filesystem Restructure - added subdirectories, removed mountDir
   */
  static getDirectoryPaths() {
    return {
      workingDir: PYTHON_DIRECTORIES.WORKING_DIR,
      tempDir: PYTHON_DIRECTORIES.TEMP_DIR,
      filesSubdir: PYTHON_DIRECTORIES.FILES_SUBDIR,
      defaultSubdir: PYTHON_DIRECTORIES.DEFAULT_SUBDIR,
      projectSubdir: PYTHON_DIRECTORIES.PROJECT_SUBDIR,
    };
  }

  /**
   * Configure basic Python environment
   * Sets working directory and adds current directory to sys.path
   *
   * @param python - Python runtime instance
   */
  static async configurePythonEnvironment(python: any): Promise<void> {
    if (!python) {
      throw new Error('Python runtime is required');
    }

    const paths = this.getDirectoryPaths();

    try {
      // Set working directory to ensure consistent file operations
      await python.runPythonAsync(`
        import os
        os.chdir('${paths.workingDir}')
      `);

      // Add subdirectories to Python path for imports (Filesystem Restructure)
      // Use absolute paths for reliable module resolution
      // Enables: import utils (from project/), from default import config, etc.
      await python.runPythonAsync(`
        import sys
        import os

        # Use absolute paths constructed from PYTHON_DIRECTORIES constants
        working_dir = '${PYTHON_DIRECTORIES.WORKING_DIR}'

        paths_to_add = [
            working_dir,                                                      # /home/pyodide
            os.path.join(working_dir, '${PYTHON_DIRECTORIES.FILES_SUBDIR}'),     # /home/pyodide/files
            os.path.join(working_dir, '${PYTHON_DIRECTORIES.DEFAULT_SUBDIR}'),   # /home/pyodide/default
            os.path.join(working_dir, '${PYTHON_DIRECTORIES.PROJECT_SUBDIR}')    # /home/pyodide/project
        ]

        for path in paths_to_add:
            if path not in sys.path:
                sys.path.insert(0, path)
      `);
    } catch (error: any) {
      throw new Error(`Environment configuration failed: ${error.message}`);
    }
  }
}
